/**
 * Configuration Migration Dialog
 * Shows migration preview and asks for user confirmation
 */

import React, { useState, useEffect } from 'react';
import { Button } from '@/components/ui/Button';
import { Modal } from '@/components/common/Modal';
import { AlertCircle, CheckCircle, ArrowRight, Info } from 'lucide-react';
import { useI18n } from '@/i18n';

interface MigrationChange {
  type: 'add' | 'remove' | 'modify';
  field: string;
  description: string;
  before?: string;
  after?: string;
}

interface ConfigMigrationDialogProps {
  isOpen: boolean;
  fromVersion: number;
  toVersion: number;
  changes: MigrationChange[];
  onConfirm: () => void;
  onCancel: () => void;
}

export const ConfigMigrationDialog: React.FC<ConfigMigrationDialogProps> = ({
  isOpen,
  fromVersion,
  toVersion,
  changes,
  onConfirm,
  onCancel,
}) => {
  const { messages } = useI18n();
  const t = messages.agentConfig.configMigration;
  const [confirmed, setConfirmed] = useState(false);

  // Reset confirmed state when dialog opens/closes
  useEffect(() => {
    if (!isOpen) {
      setConfirmed(false);
    }
  }, [isOpen]);

  const getChangeIcon = (type: MigrationChange['type']) => {
    switch (type) {
      case 'add':
        return <CheckCircle className="w-4 h-4 text-green-600" />;
      case 'remove':
        return <AlertCircle className="w-4 h-4 text-orange-600" />;
      case 'modify':
        return <ArrowRight className="w-4 h-4 text-blue-600" />;
    }
  };

  const getChangeLabel = (type: MigrationChange['type']) => {
    switch (type) {
      case 'add':
        return t?.changeTypes?.add || 'Add';
      case 'remove':
        return t?.changeTypes?.remove || 'Remove';
      case 'modify':
        return t?.changeTypes?.modify || 'Modify';
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onCancel} title={t?.title || 'Configuration Migration'} size="2xl">
      <div className="space-y-4">
        {/* Version Info */}
        <div className="flex items-center gap-3 p-3 bg-blue-50 border border-blue-200 rounded-lg">
          <Info className="w-5 h-5 text-blue-600 flex-shrink-0" />
          <div className="text-sm text-blue-900">
            <p>
              {(t?.versionInfo || 'Detected legacy configuration (v{from}). Upgrade to v{to} required for compatibility.')
                .replace('{from}', String(fromVersion))
                .replace('{to}', String(toVersion))}
            </p>
          </div>
        </div>

        {/* Migration Changes */}
        <div>
          <h4 className="text-sm font-medium text-gray-900 mb-2">
            {t?.changesTitle || 'Migration Changes:'}
          </h4>
          <div className="max-h-[300px] overflow-y-auto border border-gray-200 rounded-lg">
            <div className="divide-y divide-gray-200">
              {changes.map((change, index) => (
                <div key={index} className="p-3 hover:bg-gray-50">
                  <div className="flex items-start gap-2">
                    {getChangeIcon(change.type)}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        <span className="text-xs font-medium text-gray-600">
                          {getChangeLabel(change.type)}
                        </span>
                        <code className="text-xs font-mono bg-gray-100 px-1 py-0.5 rounded text-gray-800">
                          {change.field}
                        </code>
                      </div>
                      <p className="text-sm text-gray-700">{change.description}</p>
                      {change.before && change.after && (
                        <div className="mt-2 text-xs space-y-1">
                          <div className="flex items-center gap-2">
                            <span className="text-gray-500">{t?.before || 'Before'}:</span>
                            <code className="bg-red-50 text-red-700 px-1 py-0.5 rounded">
                              {change.before.length > 50 ? change.before.slice(0, 50) + '...' : change.before}
                            </code>
                          </div>
                          <div className="flex items-center gap-2">
                            <span className="text-gray-500">{t?.after || 'After'}:</span>
                            <code className="bg-green-50 text-green-700 px-1 py-0.5 rounded">
                              {change.after.length > 50 ? change.after.slice(0, 50) + '...' : change.after}
                            </code>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Warning */}
        <div className="flex items-start gap-2 p-3 bg-amber-50 border border-amber-200 rounded-lg">
          <AlertCircle className="w-4 h-4 text-amber-600 flex-shrink-0 mt-0.5" />
          <div className="text-xs text-amber-800">
            {t?.warning || 'After migration, you need to manually save to apply changes. Migration can be safely reverted (refresh page).'}
          </div>
        </div>

        {/* Actions */}
        <div className="flex items-center justify-end gap-3 pt-2">
          <Button variant="secondary" onClick={onCancel}>
            {t?.cancel || 'Cancel'}
          </Button>
          <Button
            variant="primary"
            onClick={() => {
              setConfirmed(true);
              onConfirm();
            }}
            disabled={confirmed}
          >
            {confirmed ? (
              <>
                <CheckCircle className="w-4 h-4 mr-1" />
                {t?.confirmed || 'Confirmed'}
              </>
            ) : (
              <>{t?.confirm || 'Confirm Migration'}</>
            )}
          </Button>
        </div>
      </div>
    </Modal>
  );
};

export default ConfigMigrationDialog;
